import { useState } from "react";
import type { Style } from "@/types/api";

interface StyleToneSelectionProps {
  selectedPlan: string;
  planLimits: Record<string, number>;
  styles: Style[];
  selectedStyles: string[];
  setSelectedStyles: (styles: string[]) => void;
}

const ROLE_LABEL: Record<string, string> = {
  main: "메인 추천",
  sub: "서브 추천",
  both: "메인·서브",
};

const ROLE_COLOR: Record<string, string> = {
  main: "bg-blue-100 text-blue-700",
  sub: "bg-purple-100 text-purple-700",
  both: "bg-teal-100 text-teal-700",
};

export default function StyleToneSelection({
  selectedPlan,
  planLimits,
  styles,
  selectedStyles,
  setSelectedStyles,
}: StyleToneSelectionProps) {
  const maxStyles = planLimits[selectedPlan] || 1;
  const [overLimitMsg, setOverLimitMsg] = useState(false);

  const handleStyleClick = (categoryKey: string) => {
    if (selectedStyles.includes(categoryKey)) {
      setSelectedStyles(selectedStyles.filter((k) => k !== categoryKey));
      setOverLimitMsg(false);
    } else if (selectedStyles.length < maxStyles) {
      setSelectedStyles([...selectedStyles, categoryKey]);
      setOverLimitMsg(false);
    } else {
      setOverLimitMsg(true);
    }
  };

  const handleStyleRemove = (categoryKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedStyles(selectedStyles.filter((k) => k !== categoryKey));
    setOverLimitMsg(false);
  };

  const isComplete = selectedStyles.length === maxStyles;

  const getStyleName = (categoryKey: string) =>
    styles.find((s) => s.categoryKey === categoryKey)?.name || categoryKey;

  return (
    <div>
      {/* Header */}
      <div className="text-center mb-10">
        <h2 className="font-serif text-3xl font-light text-[#2D2D2D] mb-4">
          스타일을 선택해주세요
        </h2>
        <p className="text-gray-500 text-sm font-light leading-relaxed mb-2">
          {maxStyles}가지 스타일을 선택할 수 있습니다.
        </p>
        <div className="flex items-center justify-center gap-3 mt-3">
          <span className="text-xs text-gray-400">
            스타일 선택:{" "}
            <span
              className={`font-semibold ${isComplete ? "text-green-600" : "text-[#C9A96E]"}`}
            >
              {selectedStyles.length}
            </span>{" "}
            / {maxStyles}
          </span>
        </div>
        {isComplete && (
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 text-xs rounded-full">
            <i className="ri-check-line"></i>
            모든 스타일 선택 완료! 다음 단계로 진행하세요
          </div>
        )}
        {overLimitMsg && (
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 text-xs rounded-full animate-pulse">
            <i className="ri-error-warning-line"></i>
            이 플랜은 최대 {maxStyles}개 스타일까지 선택 가능합니다
          </div>
        )}
      </div>

      {/* 선택 진행 요약 */}
      {selectedStyles.length > 0 && (
        <div className="mb-8 bg-[#FDF5EA] rounded-2xl p-5 border border-[#C9A96E]/30">
          <p className="text-xs font-semibold text-[#C9A96E] mb-3 uppercase tracking-wider">
            선택 현황
          </p>
          <div className="space-y-2">
            {selectedStyles.map((key, idx) => (
              <div key={key} className="flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#C9A96E] text-white text-xs font-bold shrink-0">
                  {idx + 1}
                </div>
                <span className="text-sm text-[#2D2D2D] font-medium flex-1">
                  {getStyleName(key)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 스타일 목록 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        {styles.map((style) => {
          const isSelected = selectedStyles.includes(style.categoryKey);
          const isDisabled =
            !isSelected && selectedStyles.length >= maxStyles;

          return (
            <div
              key={style.categoryKey}
              className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
                isSelected
                  ? "border-[#C9A96E] shadow-lg"
                  : isDisabled
                    ? "border-gray-200 opacity-40"
                    : "border-gray-200 hover:border-[#C9A96E]/50 hover:shadow-md"
              }`}
            >
              <div
                onClick={() => handleStyleClick(style.categoryKey)}
                className={`bg-white transition-colors duration-200 ${
                  isDisabled ? "cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                <div className="w-full h-52 overflow-hidden relative bg-gray-100 flex items-center justify-center">
                  {style.imageUrl ? (
                    <img
                      src={style.imageUrl}
                      alt={style.name}
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <span className="text-gray-300 text-sm">{style.name}</span>
                  )}
                  {isSelected && (
                    <div className="absolute inset-0 bg-[#C9A96E]/10 border-b-2 border-[#C9A96E]" />
                  )}
                  {isSelected && (
                    <div className="absolute top-3 left-3 w-7 h-7 bg-[#C9A96E] rounded-full flex items-center justify-center shadow-md">
                      <i className="ri-check-line text-sm text-white"></i>
                    </div>
                  )}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5">
                    {isSelected ? (
                      <button
                        onClick={(e) => handleStyleRemove(style.categoryKey, e)}
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-white/90 hover:bg-red-100 hover:text-red-500 text-gray-500 transition-colors duration-200 cursor-pointer shadow"
                      >
                        <i className="ri-close-line text-sm"></i>
                      </button>
                    ) : (
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white/90 border-2 border-dashed border-gray-300 text-gray-400 shadow">
                        <i className="ri-add-line text-sm"></i>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4">
                  {/* 배지 영역 */}
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {style.isPremium && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">
                        <i className="ri-vip-crown-line text-[10px]"></i>
                        프리미엄
                      </span>
                    )}
                    {style.recommendedRole && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${ROLE_COLOR[style.recommendedRole] || ""}`}
                      >
                        {ROLE_LABEL[style.recommendedRole]}
                      </span>
                    )}
                  </div>
                  <h3 className="font-serif text-base font-medium text-[#2D2D2D] mb-1">
                    {style.name}
                  </h3>
                  <p className="text-xs text-gray-500 font-light leading-relaxed">
                    {style.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* 안내 박스 */}
      <div className="mt-8 bg-[#FDF5EA] rounded-xl p-5">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 flex items-center justify-center rounded-full bg-[#C9A96E] shrink-0 mt-0.5">
            <i className="ri-information-line text-sm text-white"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-[#2D2D2D] mb-1">
              선택 방법
            </p>
            <ul className="text-xs text-gray-600 font-light leading-relaxed space-y-1">
              <li>• 원하는 스타일 카드를 클릭하면 선택됩니다</li>
              <li>• 선택한 스타일을 다시 클릭하면 선택이 해제됩니다</li>
              <li>
                • 선택한 스타일 우측의 <strong>×</strong> 버튼으로도 취소할 수
                있습니다
              </li>
              <li>
                • <span className="text-blue-600 font-medium">메인 추천</span> /
                <span className="text-purple-600 font-medium"> 서브 추천</span> 배지를 참고하여 선택하세요
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
